import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, MagnifyingGlass, FunnelSimple, DownloadSimple, X } from '@phosphor-icons/react';
import { API_BASE } from '@/lib/api';

// Every status in the backend's own VALID_STATUSES. The list previously
// offered seven of the twelve, so the stages the supervisor and AP supervisor
// work in could not be filtered to at all -- and a drill-down into one of them
// left the dropdown showing nothing.
const ALL_STATUSES = [
  'Draft',
  'Pending Supervisor Review',
  'Submitted',
  'Assigned',
  'Pending AP Processor Review',
  'Pending AP Supervisor Review',
  'Pending Accounting Manager Approval',
  'Pending General Manager Approval',
  'Approved',
  'Returned to Requestor',
  'Rejected',
  'Cancelled',
];

const STATUS_COLORS = {
  'Draft': 'bg-[#F1F5F9] text-[#475569]',
  'Pending Supervisor Review': 'bg-[#FEF9C3] text-[#854D0E]',
  'Submitted': 'bg-[#DBEAFE] text-[#1E40AF]',
  'Assigned': 'bg-[#DBEAFE] text-[#1E40AF]',
  'Pending AP Processor Review': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending AP Supervisor Review': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending Accounting Manager Approval': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending General Manager Approval': 'bg-[#FEF9C3] text-[#854D0E]',
  'Approved': 'bg-[#DCFCE7] text-[#166534]',
  'Returned to Requestor': 'bg-[#FFEDD5] text-[#9A3412]',
  'Rejected': 'bg-[#FEE2E2] text-[#991B1B]',
  'Cancelled': 'bg-[#F1F5F9] text-[#475569]'
};

const SHORT_STATUS = {
  'Pending Supervisor Review': 'Pending Supervisor',
  'Pending AP Processor Review': 'Pending AP Processor',
  'Pending AP Supervisor Review': 'Pending AP Supervisor',
  'Pending Accounting Manager Approval': 'Pending AM',
  'Pending General Manager Approval': 'Pending GM',
  'Returned to Requestor': 'Returned',
};

// Filters that live in the URL. The URL is the single source of truth: a
// drilled-down view is then a link somebody can send, and Back undoes a filter
// instead of leaving the page.
const FILTER_KEYS = ['status', 'search', 'category', 'vendor_id', 'submitted_by',
                     'date_from', 'date_to', 'amount_min', 'amount_max'];

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);
const shortDate = (v) => (v || '').slice(0, 10);

export default function PaymentRequestsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => {
    const f = {};
    FILTER_KEYS.forEach(k => { const v = searchParams.get(k); if (v) f[k] = v; });
    return f;
  }, [searchParams]);
  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = filters.status || 'all';

  // The text box is the one control that cannot read straight from the URL --
  // pushing a param on every keystroke would put a history entry per letter.
  const [searchInput, setSearchInput] = useState(filters.search || '');
  useEffect(() => { setSearchInput(filters.search || ''); }, [filters.search]);

  const setParams = useCallback((changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '' || v === 'all') next.delete(k);
      else next.set(k, v);
    });
    if (resetPage) next.delete('page');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.search || '') !== searchInput) setParams({ search: searchInput });
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    api.get('/payments', { params: { ...filters, page, limit: 30 } })
      .then(r => { setPayments(r.data.items); setTotal(r.data.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters, page]);

  // Export what is on the screen, not everything sharing its status.
  const exportData = (format) => {
    const params = new URLSearchParams(filters);
    params.set('format', format);
    window.open(`${API_BASE}/api/export/payments?${params.toString()}`, '_blank');
  };

  const vendorName = payments.find(p => p.vendor_id === filters.vendor_id)?.vendor_name;
  const chips = [];
  if (filters.status) chips.push({ k: 'status', label: filters.status.split(',').map(s => SHORT_STATUS[s] || s).join(' or ') });
  if (filters.category) chips.push({ k: 'category', label: filters.category });
  if (filters.vendor_id) chips.push({ k: 'vendor_id', label: vendorName || 'Selected vendor' });
  if (filters.submitted_by) chips.push({ k: 'submitted_by', label: filters.submitted_by === user?.id ? 'Raised by me' : 'One requestor' });
  if (filters.date_from || filters.date_to) {
    chips.push({
      k: 'date_from,date_to',
      label: filters.date_from && filters.date_to
        ? `${shortDate(filters.date_from)} to ${shortDate(filters.date_to)}`
        : filters.date_from ? `From ${shortDate(filters.date_from)}` : `Up to ${shortDate(filters.date_to)}`,
    });
  }
  if (filters.amount_min) chips.push({ k: 'amount_min', label: `From ${fmt(filters.amount_min)}` });
  if (filters.amount_max) chips.push({ k: 'amount_max', label: `Up to ${fmt(filters.amount_max)}` });

  const clearKeys = (keys) => setParams(Object.fromEntries(keys.split(',').map(k => [k, null])));
  const clearAll = () => setParams(Object.fromEntries(FILTER_KEYS.map(k => [k, null])));

  return (
    <div className="space-y-4" data-testid="payments-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Payment Requests</h1>
        <div className="flex gap-2">
          {user?.role === 'ap_accounting' && (
            <Link to="/payments/create">
              <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC] gap-2" data-testid="create-payment-btn">
                <Plus size={16} /> New Request
              </Button>
            </Link>
          )}
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => exportData('excel')} data-testid="export-excel-btn">
            <DownloadSimple size={16} /> Excel
          </Button>
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => exportData('pdf')} data-testid="export-pdf-btn">
            <DownloadSimple size={16} /> PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by code, description..."
            className="pl-9 rounded-sm h-9"
            data-testid="payments-search-input"
          />
        </div>
        <Select value={status} onValueChange={(v) => setParams({ status: v })}>
          <SelectTrigger className="w-[240px] rounded-sm h-9" data-testid="payments-status-filter">
            <FunnelSimple size={14} className="mr-2" />
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            <SelectItem value="all">All Statuses</SelectItem>
            {ALL_STATUSES.map(s => (
              <SelectItem key={s} value={s}>{SHORT_STATUS[s] || s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-[#475569]" data-testid="payments-result-count">{total} results</span>
      </div>

      {/* What you drilled into, and the way back out */}
      {chips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="active-filters">
          <span className="text-[10px] uppercase tracking-[0.1em] font-medium text-[#94A3B8]">Filtered by</span>
          {chips.map(c => (
            <button
              key={c.k}
              type="button"
              onClick={() => clearKeys(c.k)}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#0055FF]/30 bg-[#0055FF]/5 text-xs text-[#0F172A] hover:border-[#0055FF] transition-colors"
              data-testid={`filter-chip-${c.k}`}
            >
              {c.label}
              <X size={11} className="text-[#475569]" />
            </button>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-[#475569] underline hover:text-[#0055FF]"
            data-testid="clear-filters"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-sm bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F1F5F9]">
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Request Code</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Vendor</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Category</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold text-right">Amount</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Due Date</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.1em] font-semibold">Submitted By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}><TableCell colSpan={7}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              ))
            ) : payments.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#94A3B8] py-8">
                {chips.length > 0 ? 'No payment requests match these filters.' : 'No payment requests found'}
              </TableCell></TableRow>
            ) : (
              payments.map(p => (
                <TableRow key={p.id} className="hover:bg-[#F8FAFC]">
                  <TableCell>
                    <Link to={`/payments/${p.id}`} className="text-[#0055FF] hover:underline font-medium text-sm" data-testid={`payment-link-${p.request_code}`}>
                      {p.request_code}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{p.vendor_name}</TableCell>
                  <TableCell className="text-sm">{p.category}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{fmt(p.amount)}</TableCell>
                  <TableCell className="text-sm">{p.due_date}</TableCell>
                  <TableCell>
                    <span className={`inline-flex px-2 py-0.5 text-[10px] uppercase tracking-[0.05em] font-semibold rounded-sm ${STATUS_COLORS[p.status] || 'bg-[#F1F5F9] text-[#475569]'}`}>
                      {SHORT_STATUS[p.status] || p.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-[#475569]">{p.submitted_by_name}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > 30 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page <= 1}
                  onClick={() => setParams({ page: page - 1 }, { resetPage: false })}>Previous</Button>
          <span className="text-sm py-1 px-3">Page {page} of {Math.ceil(total / 30)}</span>
          <Button variant="outline" size="sm" className="rounded-sm" disabled={page >= Math.ceil(total / 30)}
                  onClick={() => setParams({ page: page + 1 }, { resetPage: false })}>Next</Button>
        </div>
      )}
    </div>
  );
}
